<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-[#22282F]/10 rounded-lg">
            <LayoutDashboard class="w-8 h-8 text-[#22282F]" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Tableau de bord</h1>
            <p class="text-dashboard-muted text-sm mt-0.5">Pilotage de l'activité — courriers, affectations, archives</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="showExport = true"
            :disabled="loading || !overview"
            class="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <FileSpreadsheet class="w-4 h-4" />
            Exporter Excel
          </button>
          <button
            @click="reload"
            :disabled="loading"
            class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#2a3139] transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw :class="['w-4 h-4', loading && 'animate-spin']" />
            Actualiser
          </button>
        </div>
      </div>

      <!-- Filtres -->
      <StatsFilterBar @change="onFilterChange" />

      <!-- Loading -->
      <div v-if="loading && !overview" class="flex items-center justify-center py-20">
        <div class="text-center">
          <div class="w-12 h-12 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
          <p class="text-dashboard-muted">Chargement des statistiques...</p>
        </div>
      </div>

      <template v-else-if="overview">
        <!-- KPI principaux -->
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <KpiCard :icon="Layers" label="Documents (total)" :value="overview.documents.total" tone="slate" />
          <KpiCard :icon="Inbox" label="Courriers arrivés" :value="overview.documents.arrives_total" tone="indigo" />
          <KpiCard :icon="Send" label="Courriers départ" :value="overview.documents.departs_total" tone="violet" />
          <KpiCard :icon="FolderKanban" label="Affectations actives" :value="overview.affectations.total" tone="amber" />
          <KpiCard :icon="ArrowLeftRight" label="Transferts" :value="overview.affectations.transferts" tone="cyan" />
          <KpiCard :icon="Archive" label="Archivés" :value="overview.archives.total" tone="emerald" />
        </div>

        <!-- Parcours (journey) -->
        <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5 mb-6">
          <h3 class="text-base font-bold text-dashboard-text mb-4 flex items-center gap-2">
            <TrendingUp class="w-5 h-5 text-[#22282F]" /> Progression des courriers (état courant)
          </h3>
          <div class="h-64">
            <ChartBar :rows="journeyRows" :color="'#4f46e5'" />
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- Courriers (arrivés) par étape —  = courriers arrivés -->
          <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5">
            <h3 class="text-base font-bold text-dashboard-text mb-1 flex items-center gap-2">
              <Inbox class="w-5 h-5 text-[#22282F]" /> Courriers par étape
              <span class="ml-auto text-sm font-semibold text-dashboard-muted"> {{ overview.documents.arrives_total }}</span>
            </h3>
            <p class="text-xs text-dashboard-muted mb-3">État courant des courriers arrivés</p>
            <div class="h-60">
              <Doughnut v-if="hasStageData" :data="stageDonutData" :options="donutOptions" />
              <div v-else class="h-full flex items-center justify-center text-sm text-dashboard-muted">
                Aucune donnée
              </div>
            </div>
          </div>

          <!-- Notes (départs) par statut —  = courriers départ -->
          <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5">
            <h3 class="text-base font-bold text-dashboard-text mb-1 flex items-center gap-2">
              <Send class="w-5 h-5 text-[#22282F]" /> Notes par statut
              <span class="ml-auto text-sm font-semibold text-dashboard-muted"> {{ overview.documents.departs_total }}</span>
            </h3>
            <p class="text-xs text-dashboard-muted mb-3">État courant des courriers départ</p>
            <div class="h-60">
              <ChartDonut :rows="noteStatusRows" />
            </div>
          </div>
        </div>

        <!-- Moteur de recherche archives (pleine largeur) -->
        <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5 mb-6">
          <h3 class="text-base font-bold text-dashboard-text mb-4 flex items-center gap-2">
            <Search class="w-5 h-5 text-[#22282F]" /> Moteur de recherche d'archives
            <span :class="['ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                           overview.recherche.operationnel ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200']">
              <span :class="['w-2 h-2 rounded-full', overview.recherche.operationnel ? 'bg-emerald-500' : 'bg-gray-400']" />
              {{ overview.recherche.operationnel ? 'Opérationnel' : 'Aucune recherche sur la période' }}
            </span>
          </h3>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MiniStat label="Recherches" :value="overview.recherche.total" />
            <MiniStat label="Latence moyenne" :value="overview.recherche.latence_moyenne_ms + ' ms'" />
            <MiniStat label="Sans résultat" :value="overview.recherche.sans_resultat_pct + ' %'" />
            <MiniStat label="Recherches IA" :value="overview.recherche.ai" />
          </div>
        </div>

        <!-- Courriers arrivés : détails -->
        <div v-if="courriers" class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Qui a importé" :icon="Upload">
            <ChartBar horizontal :rows="actorRows(courriers.par_importateur)" :color="'#0891b2'" />
          </ChartCard>
          <ChartCard title="Par ambassade" :icon="Building2">
            <ChartBar horizontal :rows="(courriers.par_ambassade || [])" :color="'#7c3aed'" />
          </ChartCard>
          <ChartCard :title="`Envoyés au SG (${courriers.envoyes_sg.total})`" :icon="Send">
            <ChartBar horizontal :rows="actorRows(courriers.envoyes_sg.par_acteur)" :color="'#d97706'" />
          </ChartCard>
          <ChartCard :title="`Envoyés à l'ambassadeur (${courriers.envoyes_ambassadeur.total})`" :icon="Send">
            <ChartBar horizontal :rows="actorRows(courriers.envoyes_ambassadeur.par_acteur)" :color="'#059669'" />
          </ChartCard>
          <ChartCard v-if="courriers.envoyes_directeur" :title="`Envoyés au directeur (${courriers.envoyes_directeur.total})`" :icon="Send">
            <ChartBar horizontal :rows="actorRows(courriers.envoyes_directeur.par_acteur)" :color="'#2563eb'" />
          </ChartCard>
        </div>

        <!-- Départs + Affectations -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div v-if="departs" class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5">
            <h3 class="text-base font-bold text-dashboard-text mb-4 flex items-center gap-2">
              <FileText class="w-5 h-5 text-[#22282F]" /> Courriers départ ({{ departs.total }})
            </h3>
            <div>
              <p class="text-xs text-dashboard-muted mb-2">Par type de document</p>
              <div class="h-52"><ChartDonut :rows="departs.par_type.map(r => ({ label: r.type, count: r.count }))" /></div>
            </div>
          </div>

          <div v-if="affectations" class="bg-white border border-dashboard-border rounded-xl shadow-sm p-5">
            <h3 class="text-base font-bold text-dashboard-text mb-1 flex items-center gap-2">
              <FolderKanban class="w-5 h-5 text-[#22282F]" /> Affectations actives ({{ affectations.total }})
            </h3>
            <p class="text-xs text-dashboard-muted mb-4">Niveau affectation</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p class="text-xs text-dashboard-muted mb-2">Par statut</p>
                <div class="h-52"><ChartDonut :rows="affectationStatusRows" /></div>
              </div>
              <div>
                <p class="text-xs text-dashboard-muted mb-2">Par direction</p>
                <div class="h-52"><ChartBar horizontal :rows="(affectations.par_direction || [])" :color="'#22282F'" /></div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Erreur -->
      <div v-else class="bg-white border border-dashboard-border rounded-xl p-12 text-center">
        <AlertCircle class="w-16 h-16 text-dashboard-muted mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-dashboard-text mb-2">Aucune donnée disponible</h3>
        <button @click="reload" class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#2a3139] transition-colors mt-2">
          Réessayer
        </button>
      </div>
    </div>

    <!-- Modale d'export Excel -->
    <ExportModal
      :open="showExport"
      :overview="overview"
      :courriers="courriers"
      :departs="departs"
      :affectations="affectations"
      :params="currentParams"
      @close="showExport = false"
      @done="onExportDone"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, h, type FunctionalComponent } from 'vue'
import {
  LayoutDashboard, RefreshCw, AlertCircle, TrendingUp, Search,
  Layers, Inbox, Send, FolderKanban, ArrowLeftRight, Archive, Upload,
  Building2, FileText, FileSpreadsheet,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import StatsFilterBar from '@/components/dashboard/StatsFilterBar.vue'
import ExportModal from '@/components/dashboard/ExportModal.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { Doughnut, Bar } from 'vue-chartjs'
import {
  Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js'

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const toast = useToast()
const loading = ref(false)
const overview = ref<any>(null)
const courriers = ref<any>(null)
const departs = ref<any>(null)
const affectations = ref<any>(null)
const currentParams = ref<Record<string, string>>({})
const showExport = ref(false)

const stageRows = computed(() =>
  (overview.value?.par_etape || []).map((s: any) => ({ label: s.label, count: s.count })),
)

// Libellés FR des vrais statuts de Note (champ libre côté backend)
const NOTE_STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  brouillon: 'Brouillon',
  en_validation: 'En validation',
  en_attente_validation: 'En attente de validation',
  validee: 'Validée',
  valide: 'Validée',
  rejetee: 'Rejetée',
  finalisee: 'Finalisée',
  en_attente_archivage: "En attente d'archivage",
  archive: 'Archivée',
  archived: 'Archivée',
}
const noteStatusLabel = (s: string) =>
  NOTE_STATUS_LABELS[(s || '').toLowerCase().trim()] || s || '—'

// Libellés FR des statuts d'Affectation (cf. Affectation.STATUT_CHOICES)
const AFFECTATION_STATUS_LABELS: Record<string, string> = {
  masque: 'Masquée',
  en_attente: 'En attente',
  accepte: 'Acceptée',
  en_rédaction: 'En rédaction',
  en_traitement: 'En traitement',
  en_consultation: 'En consultation',
  traite: 'Traitée',
  rejete: 'Rejetée',
  transferee: 'Transférée',
}
const affectationStatusLabel = (s: string) =>
  AFFECTATION_STATUS_LABELS[(s || '').toLowerCase().trim()] || s || '—'
const affectationStatusRows = computed(() =>
  (affectations.value?.par_statut || []).map((r: any) => ({
    label: affectationStatusLabel(r.statut),
    count: r.count,
  })),
)

// Donut « Notes par statut » — état courant de toutes les notes du périmètre
// (overview.departs_par_statut) pour que la somme = courriers départ (),
// au lieu de departs.par_statut qui est filtré par période de création.
const noteStatusRows = computed(() =>
  (overview.value?.departs_par_statut || []).map((r: any) => ({
    label: noteStatusLabel(r.statut),
    count: r.count,
  })),
)

// Donut « Répartition par étape »
const STAGE_COLORS = [
  '#22282F', '#4f46e5', '#7c3aed', '#d97706',
  '#0891b2', '#059669', '#dc2626', '#64748b', '#9ca3af',
]
const hasStageData = computed(() => stageRows.value.some((r) => r.count > 0))
const stageDonutData = computed(() => ({
  labels: stageRows.value.map((r) => r.label),
  datasets: [
    {
      data: stageRows.value.map((r) => r.count),
      backgroundColor: stageRows.value.map((_, i) => STAGE_COLORS[i % STAGE_COLORS.length]),
      borderWidth: 0,
    },
  ],
}))
const donutOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: {
    legend: {
      position: 'right',
      labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
    },
    tooltip: {
      callbacks: {
        label: (ctx: any) => `${ctx.label}: ${ctx.parsed}`,
      },
    },
  },
}

function qs(params: Record<string, string>) {
  return new URLSearchParams(params).toString()
}

async function fetchAll(params: Record<string, string>) {
  loading.value = true
  const query = qs(params)
  try {
    const [ov, co, de, af] = await Promise.all([
      apiFetch(`/api/admin/stats/overview/?${query}`),
      apiFetch(`/api/admin/stats/courriers/?${query}`),
      apiFetch(`/api/admin/stats/departs/?${query}`),
      apiFetch(`/api/admin/stats/affectations/?${query}`),
    ])
    overview.value = ov
    courriers.value = co
    departs.value = de
    affectations.value = af
  } catch (e) {
    toast.error('Erreur', 'Impossible de charger les statistiques')
  } finally {
    loading.value = false
  }
}

function onFilterChange(params: Record<string, string>) {
  currentParams.value = params
  fetchAll(params)
}

function reload() {
  fetchAll(currentParams.value)
}

function onExportDone(count: number) {
  toast.success('Export Excel', `${count} feuille(s) exportée(s).`)
}

// ── Petits composants présentation (functional, inline) ───────────────
const toneMap: Record<string, string> = {
  slate: 'bg-gray-100 text-gray-800',
  indigo: 'bg-indigo-50 text-indigo-700',
  violet: 'bg-violet-50 text-violet-700',
  amber: 'bg-amber-50 text-amber-700',
  cyan: 'bg-cyan-50 text-cyan-700',
  emerald: 'bg-emerald-50 text-emerald-700',
}

const KpiCard: FunctionalComponent<{ icon: any; label: string; value: number; tone: string }> = (props) =>
  h('div', { class: 'bg-white border border-dashboard-border rounded-xl shadow-sm p-4' }, [
    h('div', { class: `inline-flex p-2 rounded-lg mb-2 ${toneMap[props.tone] || toneMap.slate}` }, [
      h(props.icon, { class: 'w-5 h-5' }),
    ]),
    h('p', { class: 'text-2xl font-bold text-dashboard-text' }, String(props.value ?? 0)),
    h('p', { class: 'text-xs text-dashboard-muted' }, props.label),
  ])

const MiniStat: FunctionalComponent<{ label: string; value: any }> = (props) =>
  h('div', { class: 'bg-dashboard-bg rounded-lg p-3' }, [
    h('p', { class: 'text-lg font-bold text-dashboard-text' }, String(props.value ?? 0)),
    h('p', { class: 'text-xs text-dashboard-muted' }, props.label),
  ])

// ── Données dérivées + composants graphiques ──────────────────────────
// Progression des courriers = état courant (Courrier.status normalisé), ordonné
// en entonnoir. Source fiable & toujours cohérente (exclut les supprimés),
// contrairement au registre d'événements qui ne se remplit qu'en avant.
const PARCOURS_ORDER = [
  'brouillon', 'en_attente', 'envoye_sg', 'chez_ambassadeur', 'affecte',
  'en_traitement', 'valide', 'en_attente_archivage', 'archive',
]
const journeyRows = computed(() => {
  const stages = overview.value?.par_etape || []
  const byStage: Record<string, any> = {}
  for (const s of stages) byStage[s.stage] = s
  const rows = PARCOURS_ORDER
    .filter((st) => byStage[st])
    .map((st) => ({ label: byStage[st].label, count: byStage[st].count }))
  // Stages hors entonnoir connu (ex. « inconnu ») ajoutés en fin.
  for (const s of stages) {
    if (!PARCOURS_ORDER.includes(s.stage)) rows.push({ label: s.label, count: s.count })
  }
  return rows
})
const actorRows = (rows: any[]) =>
  (rows || []).map((r) => ({ label: r.full_name || r.username || '—', count: r.count }))

const PALETTE = [
  '#22282F', '#4f46e5', '#7c3aed', '#d97706', '#0891b2',
  '#059669', '#dc2626', '#0ea5e9', '#65a30d', '#64748b', '#9ca3af',
]
const barVOptions: any = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, x: { ticks: { font: { size: 10 } } } },
}
const barHOptions: any = {
  responsive: true, maintainAspectRatio: false, indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { ticks: { font: { size: 11 } } } },
}
const rowsHaveData = (rows: any[]) => Array.isArray(rows) && rows.some((r) => (r.count || 0) > 0)
const emptyChart = () =>
  h('div', { class: 'h-full flex items-center justify-center text-sm text-dashboard-muted' }, 'Aucune donnée')

const ChartCard: FunctionalComponent<{ title: string; icon: any }> = (props, { slots }) =>
  h('div', { class: 'bg-white border border-dashboard-border rounded-xl shadow-sm p-5' }, [
    h('h3', { class: 'text-base font-bold text-dashboard-text mb-4 flex items-center gap-2' }, [
      h(props.icon, { class: 'w-5 h-5 text-[#22282F]' }), props.title,
    ]),
    h('div', { class: 'h-60' }, slots.default ? slots.default() : []),
  ])

const ChartDonut: FunctionalComponent<{ rows: any[] }> = (props) =>
  rowsHaveData(props.rows)
    ? h(Doughnut, {
        data: {
          labels: props.rows.map((r) => r.label),
          datasets: [{
            data: props.rows.map((r) => r.count),
            backgroundColor: props.rows.map((_, i) => PALETTE[i % PALETTE.length]),
            borderWidth: 0,
          }],
        },
        options: donutOptions,
      })
    : emptyChart()

const ChartBar: FunctionalComponent<{ rows: any[]; horizontal?: boolean; color?: string }> = (props) =>
  rowsHaveData(props.rows)
    ? h(Bar, {
        data: {
          labels: props.rows.map((r) => r.label),
          datasets: [{
            data: props.rows.map((r) => r.count),
            backgroundColor: props.color || '#22282F',
            borderRadius: 4, borderWidth: 0, maxBarThickness: 30,
          }],
        },
        options: props.horizontal ? barHOptions : barVOptions,
      })
    : emptyChart()
</script>
